import { MinusIcon, PlusIcon, ProfileIcon, BabyIcon,ChildrenIcon } from "src/assets/Icons";
import { NumberControlType } from "src/components/NumberControl/NumberControl";
import { countPassengers } from "../Components/FlightSearchForm/PassengerSelection/utils";
import dayjs from "dayjs";
import { ReactNode } from "react";
import i18n from "src/i18n";

const TRIP_TYPE_OPTIONS = [
  {
    value: 1,
    key: 1,
    labelKey: "Flights.search.oneWay",
    get label() {
      return i18n.t("Flights.search.oneWay");
    },
    className: "me-2",
  },
  {
    value: 2,
    key: 2,
    labelKey: "Flights.search.roundTrip",
    get label() {
      return i18n.t("Flights.search.roundTrip");
    },
    className: "ms-2",
  },
];

const INITIAL_TRIP_OPTION = TRIP_TYPE_OPTIONS[0].value;

const PASSENGER_CLASSES = [
  {
    get label() {
      return i18n.t("Flights.search.classAll");
    },
    key: 1,
  },
  {
    get label() {
      return i18n.t("Flights.search.classEconomy");
    },
    key: 2,
  },
  {
    get label() {
      return i18n.t("Flights.search.classPremiumEconomy");
    },
    key: 3,
  },
  {
    get label() {
      return i18n.t("Flights.search.classBusiness");
    },
    key: 4,
  },
  {
    get label() {
      return i18n.t("Flights.search.classPremiumBusiness");
    },
    key: 5,
  },
  {
    get label() {
      return i18n.t("Flights.search.classFirst");
    },
    key: 6,
  },
];

type SearchFormInputs = {
  passengers: PassengersType;
  flightCabinClass: number;
  journeyType: number;
  origin?: {
    value: string;
    label: ReactNode;
  };
  destination?: {
    value: string;
    label: ReactNode;
  };
  preferredDepartureTime: string;
  preferredReturnTime: string;
  directFlight: boolean;
};

const INITIAL_SEARCH_FORM_VALUES = {
  passengers: {
    infantCount: 0,
    adultCount: 1,
    childCount: 0,
  },
  flightCabinClass: 1,
  journeyType: 1,
  origin: undefined,
  destination: undefined,
  preferredDepartureTime: dayjs().locale("en").format("YYYY-MM-DD"),
  preferredReturnTime: dayjs().locale("en").format("YYYY-MM-DD"),
  directFlight: false,
};

const INPUT_CHIP_PROPS: NumberControlType = {
  min: 0,
  max: 10,
  plusAction: <PlusIcon />,
  minusAction: <MinusIcon />,
  classNames: {
    actions: ["disabled:text-font-disabled", "disabled:text-font-disabled"],
    display: "font-senibold",
  },
};

type PassengerType = {
  key: "adultCount" | "childCount" | "infantCount";
  label: ReactNode;
  subLabel: ReactNode;
  min?: number;
  max?: number;
};

const PASSENGER_TYPES: PassengerType[] = [
  {
    key: "adultCount",
    get label() {
      return <p className="font-bold">{i18n.t("Flights.search.adults")}</p>;
    },
    get subLabel() {
      return (
        <div className="flex items-center gap-1">
          <ProfileIcon className="h-5 w-5" />
          <p className="text-sm"> {i18n.t("Flights.search.adultsAgeRange")}</p>
        </div>
      );
    },
    min: 1,
  },
  {
    key: "childCount",
    get label() {
      return <p className="font-bold">{i18n.t("Flights.search.children")}</p>;
    },
    get subLabel() {
      return (
        <div className="flex items-center gap-1">
          <ChildrenIcon className="h-3 w-3" />
          <p className="text-sm">{i18n.t("Flights.search.childrenAgeRange")}</p>
        </div>
      );
    },
  },
  {
    key: "infantCount",
    get label() {
      return <p className="font-bold">{i18n.t("Flights.search.infants")}</p>;
    },
    get subLabel() {
      return (
        <div className="flex items-center gap-1">
          <BabyIcon className="h-4 w-4" />
          <p className="text-sm"> {i18n.t("Flights.search.infantsAgeRange")}</p>
        </div>
      );
    },
  },
];

type PassengersType = Record<(typeof PASSENGER_TYPES)[number]["key"], number>;

const INITIAL_PASSENGERS = PASSENGER_TYPES.reduce((prev, next) => {
  return {
    ...prev,
    [next.key]: next.min ?? INPUT_CHIP_PROPS?.min ?? 0,
  };
}, {} as PassengersType);

type ValidationRule = {
  name: string;
  description?: string;
  rule: (passengers: PassengersType) => boolean;
};

const VALIDATION_RULES: ValidationRule[] = [
  {
    name: "InfantsAdults",
    description: "Infants must not exceed adults",
    rule: (p) => p.infantCount <= p.adultCount,
  },
  {
    name: "TotalPassengers",
    description: "Total passengers limit",
    rule: (p) => countPassengers(p) <= 5,
  },
];

type PassengerClass = (typeof PASSENGER_CLASSES)[number];
const INITIAL_CLASS = PASSENGER_CLASSES[0];

type airportResponseType = {
  cityIataCode: string;
  cityName: string;
  countryIso2: string;
  countryName: string;
  iataCode: string;
  icaoCode: string;
  name: string;
  airport: string;
};

type Segment = {
  origin: string;
  destination: string;
  preferredDepartureTime?: string;
  flightCabinClass: number;
};

type FlightSearchRequestType = {
  passengers: PassengersType;
  journeyType: number;
  segments: Segment[];
};

export {
  PASSENGER_CLASSES,
  INITIAL_CLASS,
  TRIP_TYPE_OPTIONS,
  INITIAL_TRIP_OPTION,
  INITIAL_SEARCH_FORM_VALUES,
  PASSENGER_TYPES,
  INPUT_CHIP_PROPS,
  INITIAL_PASSENGERS,
  VALIDATION_RULES,
};

export type { PassengerClass, SearchFormInputs, PassengersType, ValidationRule, airportResponseType, FlightSearchRequestType };
